import React, {createContext, useContext, useState, useEffect} from 'react';
import {loginApi, registerApi} from '../../utils/api';
import {getAccountProfile} from '../../utils/accountApi';

const AUTH_USER_KEY = 'koenig-auth-user';
const AUTH_TOKENS_KEY = 'koenig-auth-tokens';

const AuthContext = createContext(null);

export function AuthProvider({children}) {
    const [user, setUser] = useState(null);
    const [tokens, setTokens] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const savedUser = localStorage.getItem(AUTH_USER_KEY);
        const savedTokens = localStorage.getItem(AUTH_TOKENS_KEY);
        if (savedUser && savedTokens) {
            try {
                setUser(JSON.parse(savedUser));
                setTokens(JSON.parse(savedTokens));
            } catch {
                localStorage.removeItem(AUTH_USER_KEY);
                localStorage.removeItem(AUTH_TOKENS_KEY);
            }
        }
        setIsLoading(false);
    }, []);

    const persistAuth = (userData, tokenData) => {
        setUser(userData);
        setTokens(tokenData);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
        localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(tokenData));
    };

    /**
     * Login with backend API.
     * @param {{ email: string, password: string }} credentials
     * @returns {Promise<{ ok: boolean, error?: string }>}
     */
    const login = async (credentials) => {
        const result = await loginApi(credentials);
        if (result.ok && result.data) {
            persistAuth(result.data.user, result.data.tokens);
            return {ok: true};
        }
        return {ok: false, error: result.error || 'Login failed'};
    };

    /**
     * Register with backend API and log in.
     * @param {{ name: string, email: string, password: string }} credentials
     * @returns {Promise<{ ok: boolean, error?: string }>}
     */
    const signup = async (credentials) => {
        const result = await registerApi(credentials);
        if (result.ok && result.data) {
            persistAuth(result.data.user, result.data.tokens);
            return {ok: true};
        }
        return {ok: false, error: result.error || 'Registration failed'};
    };

    const logout = () => {
        setUser(null);
        setTokens(null);
        localStorage.removeItem(AUTH_USER_KEY);
        localStorage.removeItem(AUTH_TOKENS_KEY);
    };

    /**
     * Refresh user profile from API (e.g. after account settings update).
     * @returns {Promise<{ ok: boolean, error?: string }>}
     */
    const refreshUser = async () => {
        if (!tokens?.accessToken) return { ok: false, error: 'Not authenticated' };
        const result = await getAccountProfile({ accessToken: tokens.accessToken });
        if (result.ok && result.data?.user) {
            persistAuth(result.data.user, tokens);
            return { ok: true };
        }
        return { ok: false, error: result.error || 'Failed to refresh profile' };
    };

    const value = {
        user,
        tokens,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
